'use strict';

import { useState } from 'react';
import Downshift from 'downshift';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/pro-regular-svg-icons/faCircleNotch';
import { postJson } from 'shared/fetch.js';

function SearchPanel({ loading, engine, onHide = () => null }) {
	const [results, setResults] = useState([]);
	const [querying, setQuerying] = useState(false);
	
	if (loading) {
		return (
			<div className="m-4 antialiased">
				Loading…
			</div>
		);
	}
	
	const onInput = (term) => {
		if ('' === term) {
			return;
		}
		
		setQuerying(true);
		
		const url = `https://search-api.swiftype.com/api/v1/public/installs/${ engine }/suggest.json`;
		
		postJson(url, { q: term })
			.then(result => result.json())
			.then(data => {
				const results = 'records' in data && 'page' in data.records
					? data.records.page
					: [];
				
				setResults(results);
				setQuerying(false);
			});
	};
	
	const renderDownshift = (downshift) => {
		const { getInputProps, getLabelProps, getMenuProps, inputValue, isOpen } = downshift;
		
		const show_results = isOpen && results.length > 0;
		const hide_results = true !== show_results;
		
		const label_props = getLabelProps({
			className: isOpen
				? 'sr-only'
				: "font-condensed my-4 text-lg text-center text-gray-400 text-center block"
		});
		
		const input_props = getInputProps({
			autoFocus: true,
			className: "block w-full mb-4 p-2 px-4 text-base leading-normal bg-white border rounded-full text-gray-700 border-gray-300",
			placeholder: "Start typing to search all of NACHI.ORG",
			onKeyUp: event => {
				if ('Escape' === event.key && '' === inputValue) {
					onHide();
				}
			},
		});
		
		const menu_props = getMenuProps({
			className: show_results ? 'mt-4 pt-4 border-t list-reset overflow-y-auto overflow-x-hidden' : 'hidden',
			style: {
				maxHeight: `${ window.innerHeight * .75 }px`
			},
		}, { suppressRefError: true });
		
		const renderDownshiftItem = (record, index) => {
			const { getItemProps, highlightedIndex, selectedItem } = downshift;
			const is_highlighted = highlightedIndex === index;
			
			const base_class = 'block p-2 w-full rounded cursor-pointer overflow-hidden';
			
			const props = getItemProps({
				index,
				item: record,
				className: is_highlighted
					? `bg-blue-100 ${ base_class }`
					: `bg-white ${ base_class }`,
			});
			
			const highlight = html => html.replace(/<em>/g, '<em class="not-italic font-bold text-blue-700">');
			
			const title = 'title' in record.highlight
				? <span dangerouslySetInnerHTML={ { __html: highlight(record.highlight.title) } } />
				: record.title;
			
			const subtitle = 'sections' in record.highlight
				? <div className="text-sm opacity-70" dangerouslySetInnerHTML={ { __html: highlight(record.highlight.sections) } } />
				: null;
			
			return (
				<li key={ record.id } { ...props }>
					<div className="mb-1 font-condensed">
						{ title }
					</div>
					<div className="mb-1 text-xs text-green-700 opacity-60">
						{ record.url }
					</div>
					{ subtitle }
				</li>
			);
		};
		
		return (
			<div className="text-left" style={ { width: `${ window.innerWidth < 450 ? window.innerWidth : 450 }px` } }>
				<label { ...label_props }>
					Search NACHI.ORG:
				</label>
				<div className="relative">
					<input { ...input_props } />
					{ querying && (
						<div className="flex absolute top-0 right-0 bottom-0 items-center pr-4">
							<Icon icon={ faCircleNotch } className="text-xl opacity-20 svg-spin" />
						</div>
					) }
				</div>
				<ul { ...menu_props }>
					{ show_results && results.map(renderDownshiftItem) }
				</ul>
				{ hide_results && (
					<div className="pt-4 mt-8 border-t">
						<p className="font-sans text-sm text-center text-gray-300">
							Other search options:
						</p>
						<div className="flex justify-center mt-1">
							<a className="mx-1 btn btn-blue btn-link" href="https://forum.nachi.org/search" target="_blank">
								Search the Forum
							</a>
							<a className="mx-1 btn btn-blue btn-link" href="/certified-inspectors" target="_blank">
								Find a Certified Inspector
							</a>
							<a className="mx-1 ml-auto btn btn-blue btn-link" href="/search">
								More…
							</a>
						</div>
					</div>
				) }
			</div>
		);
	};
	
	return (
		<div className="p-2 tailwind">
			<Downshift
				onChange={ selection => {
					window.location.href = selection.url;
				} }
				onInputValueChange={ onInput }
				itemToString={ item => (item ? item.title : '') }
				children={ renderDownshift }
			/>
		</div>
	);
}

export default SearchPanel;
